import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Building2,
  Image as ImageIcon,
  Loader2,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useQuery, useMutation } from "@tanstack/react-query";
import { BASE_URL } from "utils/constant";

const INDUSTRIES = [
  "Construction",
  "Interior Design",
  "Architecture",
  "Real Estate",
  "Infrastructure",
  "Other",
];

const TEAM_SIZES = ["1-5", "5-20", "20+"];

const OrganizationDetailsForm = () => {
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");
  const [industry, setIndustry] = useState("");
  const [teamSize, setTeamSize] = useState("1-5");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isCreated, setIsCreated] = useState(false);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [tempImageType, setTempImageType] = useState("image/jpeg");
  const [tempImageName, setTempImageName] = useState("photo.jpg");
  const imgRef = React.useRef(null);
  const accessToken = localStorage.getItem("access");

  // Helper function to trim filename if it exceeds 100 characters
  const trimFileName = (file) => {
    if (file.name.length <= 100) {
      return file;
    }

    const extension = file.name.split(".").pop();
    const nameWithoutExtension = file.name.substring(
      0,
      file.name.lastIndexOf(".")
    );
    const maxNameLength = 100 - extension.length - 1; // -1 for the dot
    const trimmedName = nameWithoutExtension.substring(0, maxNameLength);

    // Create a new file with trimmed name
    const trimmedFile = new File([file], `${trimmedName}.${extension}`, {
      type: file.type,
      lastModified: file.lastModified,
    });

    return trimmedFile;
  };

  const getCropFileMeta = (file) => {
    if (file.type !== "image/svg+xml") {
      return {
        type: file.type || "image/jpeg",
        name: file.name || "photo.jpg",
      };
    }

    const baseName = (file.name || "photo").replace(/\.[^/.]+$/, "");
    return {
      type: "image/png",
      name: `${baseName}.png`,
    };
  };

  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: ["user-details"],
    queryFn: async () => {
      const { data } = await axios.get(
        `${BASE_URL}/auth/user-details-of-user/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken?.trim()}`,
          },
        }
      );
      return data;
    },
    enabled: !!accessToken,
  });

  const createOrganization = useMutation({
    mutationFn: async ({ userId, orgName, logoFile }) => {
      const form = new FormData();
      form.append("owner", userId);
      form.append("organization_name", orgName);
      form.append("active_flag", true);
      if (logoFile) {
        // Trim filename before appending
        const trimmedFile = trimFileName(logoFile);
        form.append("logo_image_file", trimmedFile);
      }

      const { data } = await axios.post(
        `${BASE_URL}/org/create-organization/`,
        form,
        {
          headers: {
            Authorization: `Bearer ${accessToken.trim()}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return data;
    },
    onSuccess: (data) => {
      localStorage.setItem("organization", JSON.stringify(data.data));
      window.dispatchEvent(new Event("organizationChanged"));
      toast.success("Organization created successfully");
      setIsCreated(true);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create organization"
      );
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      // Validate file size (optional - e.g., 5MB limit)
      const maxSize = 15 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error("File size should be less than 15MB");
        return;
      }

      const cropFileMeta = getCropFileMeta(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageType(cropFileMeta.type);
        setTempImageName(cropFileMeta.name);
        setTempImageSrc(reader.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
      if (e.target) {
        e.target.value = "";
      }

      // Show filename info if it was trimmed
      if (file.name.length > 100) {
        const trimmedFile = trimFileName(file);
        toast.info(`Filename was trimmed to: ${trimmedFile.name}`);
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    handleFileChange({ target: { files: [file], value: "" } });
  };

  const handleRemoveImage = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleAdjustPhoto = () => {
    if (logoFile) {
      setTempImageType(logoFile.type || "image/jpeg");
      setTempImageName(logoFile.name || "photo.jpg");
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageSrc(reader.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(logoFile);
    } else if (logoPreview) {
      setTempImageType("image/jpeg");
      setTempImageName("photo.jpg");
      setTempImageSrc(logoPreview);
      setIsCropperOpen(true);
    }
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc,
    pixelCrop,
    mimeType = "image/jpeg"
  ) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;
    const renderedRect = imgRef.current?.getBoundingClientRect();
    const renderedWidth = renderedRect?.width || imgRef.current?.width || image.width;
    const renderedHeight =
      renderedRect?.height || imgRef.current?.height || image.height;

    const scaleX = naturalWidth / renderedWidth;
    const scaleY = naturalHeight / renderedHeight;

    const outputWidth = Math.max(1, Math.floor(pixelCrop.width));
    const outputHeight = Math.max(1, Math.floor(pixelCrop.height));
    const sx = Math.max(
      0,
      Math.min(naturalWidth - 1, Math.floor(pixelCrop.x * scaleX))
    );
    const sy = Math.max(
      0,
      Math.min(naturalHeight - 1, Math.floor(pixelCrop.y * scaleY))
    );
    const sourceWidth = Math.max(
      1,
      Math.min(naturalWidth - sx, Math.floor(pixelCrop.width * scaleX))
    );
    const sourceHeight = Math.max(
      1,
      Math.min(naturalHeight - sy, Math.floor(pixelCrop.height * scaleY))
    );

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    ctx.drawImage(
      image,
      sx,
      sy,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        mimeType,
        0.92
      );
    });
  };

  const handleCropCancel = () => {
    setIsCropperOpen(false);
    setCompletedCrop(null);
    setTempImageSrc(null);
    setCrop(undefined);
  };

  const handleCropConfirm = async () => {
    try {
      const normalizeToPixelCrop = (cropValue) => {
        if (!cropValue) return null;

        if (cropValue.unit === "%") {
          const renderedRect = imgRef.current?.getBoundingClientRect();
          const renderedWidth = renderedRect?.width || imgRef.current?.width || 0;
          const renderedHeight =
            renderedRect?.height || imgRef.current?.height || 0;
          if (!renderedWidth || !renderedHeight) return null;

          return {
            unit: "px",
            x: (cropValue.x / 100) * renderedWidth,
            y: (cropValue.y / 100) * renderedHeight,
            width: (cropValue.width / 100) * renderedWidth,
            height: (cropValue.height / 100) * renderedHeight,
          };
        }

        return cropValue;
      };

      const pixelCrop = normalizeToPixelCrop(completedCrop || crop);
      if (!tempImageSrc || !pixelCrop || !pixelCrop.width || !pixelCrop.height)
        return;

      const blob = await getCroppedImg(tempImageSrc, pixelCrop, tempImageType);
      const croppedFile = new File([blob], tempImageName, {
        type: tempImageType,
      });
      setLogoFile(croppedFile);

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        handleCropCancel();
      };
      reader.readAsDataURL(croppedFile);
    } catch (err) {
      console.error("Cropping failed", err);
      toast.error("Failed to apply crop");
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    if (!width || !height) return;
    setCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 });
    setCompletedCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 });
  };

  const handleSave = () => {

    if (!userData?.data?.id || orgName.trim() === "") {
      toast.error("Missing required information");
      return;
    }
    // console.log("Saving organization...");
    createOrganization.mutate({
      userId: userData.data.user,
      orgName,
      logoFile,
    });
  };

  const ownerName =
    [userData?.data?.first_name, userData?.data?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() || "Owner";

  return (
    <>
      <style>{`
        @keyframes sm-confetti-fall {
          0% { transform: translate3d(0, -18vh, 0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate3d(var(--drift), 105vh, 0) rotate(720deg); opacity: 0; }
        }
      `}</style>

      <div
        className="fixed inset-0 z-50 min-h-screen overflow-y-auto bg-[#fbf9ff] px-6 py-6 text-[#160d25]"
        style={{
          backgroundImage:
            "linear-gradient(#eeeaf4 1px, transparent 1px), linear-gradient(90deg, #eeeaf4 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      >
        {isCreated ? (
          <div className="relative flex items-center justify-center overflow-hidden">
            {Array.from({ length: 28 }).map((_, index) => (
              <span
                key={index}
                className="pointer-events-none absolute top-0 h-3 w-2 rounded-sm"
                style={{
                  left: `${(index * 37) % 100}%`,
                  backgroundColor: ["#3B0069", "#7c5fd6", "#f59e0b", "#22c55e", "#f43f5e"][index % 5],
                  animation: `sm-confetti-fall ${2.8 + (index % 5) * 0.35}s linear ${index * 0.08}s infinite`,
                  "--drift": `${index % 2 ? "" : "-"}${24 + (index % 7) * 9}px`,
                }}
              />
            ))}

            <div className="relative z-10  mt-12 flex w-full max-w-4xl flex-col items-center text-center">
              <div className="mb-9 flex size-20 items-center justify-center rounded-3xl bg-gradient-to-br from-[#3B0069] to-[#8267dc] text-white shadow-2xl shadow-purple-300/60">
                <Building2 className="size-10" />
              </div>
              <h1 className="text-xl font-extrabold tracking-tight text-[#160d25] md:text-3xl">
                Your workspace is ready!
              </h1>
              <p className="mt-3 text-lg text-[#746f84]">
                Your digital headquarters is set up. Let's launch your workspace.
              </p>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="mt-14 inline-flex items-center justify-center gap-4 rounded-xl bg-gradient-to-r from-[#3B0069] to-[#8267dc] px-10 py-3 text-xl font-bold text-white shadow-2xl shadow-purple-300/50 transition hover:scale-[1.01]"
              >
                Launch Your Workspace
                <ArrowRight className="size-9" />
              </button>
              <p className="mt-6 text-sm text-[#746f84]">
                Set up your plan, team, and first project in 3 quick steps.
              </p>
            </div>
          </div>
        ) : (
          <div className="mx-auto grid w-full max-w-7xl items-start gap-12 lg:grid-cols-2">
            <section className="rounded-xl border mt-6 border-[#ded9e8] bg-white shadow-xl shadow-purple-100/50">
              <div className="h-1 rounded-t-2xl bg-gradient-to-r from-[#3B0069] via-[#8b6ee8] to-[#f97316]" />
              <div className="p-4 md:p-5">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex size-14 items-center justify-center rounded-2xl bg-[#ede8f3] text-[#3B0069]">
                    <Building2 className="size-7" />
                  </div>
                  <div>
                    <h1 className="text-lg font-bold tracking-tight md:text-[22px]">
                      Create your organization
                    </h1>
                    <p className="mt-1 text-sm text-[#746f84]">
                      Set up your digital headquarters
                    </p>
                  </div>
                </div>

                {isUserLoading && (
                  <p className="mb-4 rounded-lg bg-purple-50 px-4 py-3 text-sm text-[#3B0069]">
                    Loading user details...
                  </p>
                )}

                <div className="space-y-6">
                  <div>
                    <label className="mb-2 block text-sm font-semibold" htmlFor="orgName">
                      Organization Name
                    </label>
                    <input
                      id="orgName"
                      type="text"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder="Royal Pvt Ltd"
                      className="w-full rounded-xl border-2 border-[#ded9e8] bg-[#fbf9ff] px-6 py-2 text-sm outline-none transition focus:border-[#3B0069] focus:ring-4 focus:ring-purple-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold" htmlFor="industry">
                      Industry
                    </label>
                    <select
                      id="industry"
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      className="w-full rounded-xl border-2 border-[#ded9e8] bg-[#fbf9ff] px-6 py-2 text-sm outline-none transition focus:border-[#3B0069] focus:ring-4 focus:ring-purple-100"
                    >
                      <option value="">Select industry</option>
                      {INDUSTRIES.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
{/* 
                  <div>
                    <div className="mb-2 text-sm font-semibold">Team Size</div>
                    <div className="grid grid-cols-3 gap-4">
                      {TEAM_SIZES.map((size) => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => setTeamSize(size)}
                          className={`rounded-xl border-2 px-6 py-2 text-sm font-semibold transition ${
                            teamSize === size
                              ? "border-[#3B0069] bg-purple-50 text-[#3B0069]"
                              : "border-[#ded9e8] bg-white text-[#746f84] hover:border-[#b9abc8]"
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div> */}

                  <div>
                    <div className="mb-3 text-sm font-semibold">Upload Company Signage</div>
                    <div
                      className="relative flex min-h-[100px] items-center justify-center rounded-xl border-2 border-dashed border-[#ded9e8] bg-white"
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={handleDrop}
                    >
                      {logoPreview ? (
                        <div className="relative flex h-full w-full items-center justify-center p-6">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="max-h-36 max-w-full rounded-xl object-contain"
                          />
                          <button
                            onClick={handleAdjustPhoto}
                            className="absolute right-16 top-4 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50"
                            type="button"
                          >
                            Adjust
                          </button>
                          <button
                            onClick={handleRemoveImage}
                            className="absolute right-4 top-4 rounded-full bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
                            type="button"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex h-full w-full cursor-pointer flex-col items-center justify-center py-4 text-center">
                          <Upload className="mb-3 size-10 text-[#746f84]" />
                          <span className="text-sm font-medium text-[#746f84]">
                            Drag & drop or click to upload
                          </span>
                          <span className="mt-2 text-sm text-[#aaa5b5]">
                            Your company logo or signage
                          </span>
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                    {logoFile && (
                      <p className="mt-3 text-sm text-[#746f84]">
                        Selected file size: {(logoFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleSave}
                  disabled={isUserLoading || createOrganization.isPending || !orgName.trim()}
                  className="mt-10 flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#b6a2cd] to-[#c4b8f2] px-6 py-3 text-lg font-bold text-white shadow-xl shadow-purple-100 transition enabled:from-[#3B0069] enabled:to-[#8267dc] enabled:hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
                  type="button"
                >
                  {createOrganization.isPending ? (
                    <Loader2 className="size-6 animate-spin" />
                  ) : (
                    <ArrowRight className="size-6" />
                  )}
                  Create Organization
                </button>
                <p className="mt-2 text-center text-sm text-[#746f84]">
                  You can edit this later in settings.
                </p>
              </div>
            </section>

            <aside className="pt-1 lg:sticky lg:top-8">
              <div className="mb-4 flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.15em] text-[#746f84]">
                <span className="size-3 rounded-full bg-[#f2b88f]" />
                Organization Preview
              </div>
              <div className="rounded-xl border border-[#ded9e8] bg-white shadow-xl shadow-purple-100/50">
                <div className="h-1 rounded-t-2xl bg-gradient-to-r from-[#3B0069] via-[#8b6ee8] to-[#f97316]" />
                <div className="p-8">
                  <div className="mb-6 flex items-center gap-5">
                    <div className="flex size-20 items-center justify-center overflow-hidden rounded-full bg-[#ede8f3] text-[#746f84]">
                      {logoPreview ? (
                        <img src={logoPreview} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <ImageIcon className="size-9" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-lg font-extrabold">
                        <Building2 className="size-5 shrink-0 text-[#746f84]" />
                        <span className="truncate">{orgName.trim() || "Your Company"}</span>
                      </div>
                      {/* {teamSize && (
                        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-[#3B0069]">
                          <Sparkles className="size-3" />
                          Team {teamSize}
                        </div>
                      )} */}
                    </div>
                  </div>

                  <dl className="divide-y divide-[#ded9e8] text-sm">
                    <div className="flex items-center justify-between gap-6 py-3">
                      <dt className="text-[#746f84]">Owner</dt>
                      <dd className="font-semibold text-[#160d25]">{ownerName}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-6 py-3">
                      <dt className="text-[#746f84]">Industry</dt>
                      <dd className="font-semibold text-[#160d25]">{industry || "-"}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-6 py-3">
                      <dt className="text-[#746f84]">Members</dt>
                      <dd className="font-semibold text-[#160d25]">1</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>

      {isCropperOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={handleCropCancel}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] max-w-xl h-[60vh] flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-base font-semibold">Adjust Photo</h4>
              <button
                onClick={handleCropCancel}
                className="text-gray-500 hover:text-gray-700"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="relative flex-1 bg-gray-100 overflow-auto">
              {tempImageSrc && (
                <div className="w-full h-full flex items-center justify-center p-4">
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    keepSelection
                    className="max-w-full max-h-full"
                  >
                    <img
                      ref={imgRef}
                      src={tempImageSrc}
                      alt="to-crop"
                      onLoad={onImageLoad}
                      className="max-h-[45vh] max-w-full w-auto h-auto object-contain"
                    />
                  </ReactCrop>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={handleCropCancel}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-800 hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                className="px-4 py-2 rounded-md bg-[#3B0069] text-white hover:bg-purple-700"
                type="button"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganizationDetailsForm;
